/** Pure layout and data helpers for the opportunity matrix (OpportunityMatrix). */

import type { Industry, Verdict } from '@/api/types';

import { OUTCOME_STYLE } from './ideaUi';
import type { MatrixPoint } from './matrixAxes';

export type DotKind = Verdict | 'none';

export const DOT_KINDS: { kind: DotKind; label: string }[] = [
  { kind: 'go', label: OUTCOME_STYLE.go.label },
  { kind: 'conditional_go', label: OUTCOME_STYLE.conditional_go.label },
  { kind: 'no_go', label: OUTCOME_STYLE.no_go.label },
  { kind: 'none', label: 'No verdict' },
];

export const QUADRANTS = {
  topLeft: 'Quick wins',
  topRight: 'Priorities',
  bottomLeft: 'Deprioritize',
  bottomRight: 'Strategic bets',
};

export const MARGIN = { top: 12, right: 16, bottom: 44, left: 52 };
export const DOT_R = 6;
// Coordinates this close (in score points) count as the same spot.
const SAME_SPOT = 0.5;

export const dotKind = (p: MatrixPoint): DotKind => p.idea.verdict ?? 'none';

export const dotKindLabel = (kind: DotKind): string => DOT_KINDS.find((d) => d.kind === kind)!.label;

/** A verdict's mark centred on (cx, cy): circle, diamond, triangle; a ring for no verdict. */
export function markPath(kind: DotKind, cx: number, cy: number, r: number): string {
  switch (kind) {
    case 'conditional_go': {
      const d = r * 1.3;
      return `M${cx} ${cy - d}L${cx + d} ${cy}L${cx} ${cy + d}L${cx - d} ${cy}Z`;
    }
    case 'no_go': {
      const d = r * 1.35;
      return `M${cx} ${cy - d}L${cx + d * 0.95} ${cy + d * 0.65}L${cx - d * 0.95} ${cy + d * 0.65}Z`;
    }
    default:
      return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0Z`;
  }
}

/** The lower end of both axes: a round number a little under the lowest point, at most 50. */
function lowerBound(points: MatrixPoint[], threshold: number): number {
  const min = Math.min(...points.flatMap((p) => [p.impact, p.feasibility]), threshold);
  return Math.max(0, Math.min(50, Math.floor((min - 5) / 10) * 10));
}

export interface MatrixScale {
  height: number;
  plotW: number;
  plotH: number;
  narrow: boolean;
  ticks: number[];
  tx: number;
  ty: number;
  x: (v: number) => number;
  y: (v: number) => number;
}

/** Pixel layout for a plot `width` wide: drawn in pixels so labels keep their size on any screen. */
export function matrixScale(width: number, compact: boolean, points: MatrixPoint[], threshold: number): MatrixScale {
  const height = Math.round(Math.min(compact ? 320 : 540, Math.max(260, width * (compact ? 0.62 : 0.6))));
  const lo = lowerBound(points, threshold);
  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotH = height - MARGIN.top - MARGIN.bottom;
  const x = (v: number) => MARGIN.left + ((v - lo) / (100 - lo)) * plotW;
  const y = (v: number) => MARGIN.top + (1 - (v - lo) / (100 - lo)) * plotH;
  const ticks = Array.from({ length: Math.floor((100 - lo) / 10) + 1 }, (_, i) => lo + i * 10);
  return { height, plotW, plotH, narrow: plotW < 420, ticks, tx: x(threshold), ty: y(threshold), x, y };
}

/**
 * Highest totals drawn last, so they sit on top where dots overlap; the active
 * dot above all. `sameSpot` is every other idea at the active dot's spot.
 */
export function drawOrder(points: MatrixPoint[], active: string | null) {
  const ordered = [...points].sort((a, b) => (a.idea.total ?? 0) - (b.idea.total ?? 0));
  const activePoint = ordered.find((p) => p.idea.id === active) ?? null;
  if (!activePoint) return { activePoint, drawn: ordered, sameSpot: [] };
  const drawn = [...ordered.filter((p) => p !== activePoint), activePoint];
  const sameSpot = points.filter(
    (p) =>
      p !== activePoint &&
      Math.abs(p.impact - activePoint.impact) <= SAME_SPOT &&
      Math.abs(p.feasibility - activePoint.feasibility) <= SAME_SPOT,
  );
  return { activePoint, drawn, sameSpot };
}

export function verdictCounts(points: MatrixPoint[]): Record<DotKind, number> {
  const counts = Object.fromEntries(DOT_KINDS.map(({ kind }) => [kind, 0])) as Record<DotKind, number>;
  points.forEach((p) => (counts[dotKind(p)] += 1));
  return counts;
}

/** A point's colour class when colouring by industry: its industry's slot, else "Other". */
export function colourOf(p: MatrixPoint, slots: Map<string, number>): string {
  const slot = p.idea.industry ? slots.get(p.idea.industry.id) : undefined;
  return slot ? `series-${slot}` : 'series-other';
}

export function industryLegend(points: MatrixPoint[], industries: Industry[], slots: Map<string, number>) {
  return [
    ...[...slots].map(([id, slot]) => ({
      colour: `series-${slot}`,
      label: industries.find((i) => i.id === id)!.name,
      count: points.filter((p) => p.idea.industry?.id === id).length,
    })),
    { colour: 'series-other', label: 'Other', count: points.filter((p) => colourOf(p, slots) === 'series-other').length },
  ];
}
